import { BinaryTreeNode } from './binary-tree-node';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import type { BTNPAR } from './binary-tree-node';
import { BinaryTreeEdit } from './binary-tree-edit';
import { BinaryTreeStorage } from './binary-tree-storage';
import { debounceTime, Subject } from 'rxjs';
import { ScriptTags } from './script-tags';
import { ScriptRatings } from './script-ratings';
import { AutoUser } from './auto-user';
import { XItem } from './x-item';

export type BTTYPE = 'ratings' | 'tags';

export type BTMETA = {
    title?: string;
    description?: string;
    type?: BTTYPE;
    script?: string;
    isOn?: boolean; //new
};

export type BTPAR = {
    id?: string;
    ownerId?: string;
    meta?: BTMETA;
    root?: BTNPAR;
};

export type TEMPLATE_NODE = {
    id?: string;
    title: string;
    description?: string;
    children?: TEMPLATE_NODE[];
};

export class BinaryTree {
    id: string;
    ownerId: string;
    meta: BTMETA = {
        type: 'ratings',
        title: '',
        description: '',
        script: '',
    };

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    root: BinaryTreeNode;
    private _compiledOptionFun?: Function;

    private _bts: BinaryTreeStorage | undefined;
    searchParams = {
        includeSet: new Set<BinaryTreeNode>(),
        excludeSet: new Set<BinaryTreeNode>(),
    };

    onSetMap = new Map<string, Set<string>>();

    private _isEditMode = false;
    private _isModified = false;
    private _btEdit: BinaryTreeEdit | undefined;

    constructor(
        public coords: CoordinatorService,
        params: BTPAR,
    ) {
        this.id = params.id || this.coords.uniqueId('');
        this.ownerId = params.ownerId || '_current';
        this.meta = Object.assign({}, params.meta);
        this.root = new BinaryTreeNode(this, params.root || {});

        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    get script() {
        return this.meta.script || '';
    }

    set script(val: string) {
        this.meta.script = val;
    }

    get isDisabled() {
        return this.testItem(this.coords.resultList$.activeItem!) === 'disable';
    }

    get isHidden() {
        return this.testItem(this.coords.resultList$.activeItem!) === 'hide';
    }

    saveAsScript() {
        const f = this.type === 'ratings' ? 'ratings' : 'tags';
        const txt = `
        let title = "${this.title}"
        const origTitle = title
        let i = 1;
        while (${f}(title)) {
            title = \`\${origTitle} (\${i++})\`
        }
        const rt = ${f}(title, true)
        ${this.root.forSaveScript()}
        `;

        this.coords.filesystem$.saveFile2(
            `${this.type}.mscript`,
            new Blob([txt], { type: 'text/plain' }),
        );
    }

    private _compileOptionFun() {
        if (!this.script?.trim()) return;
        try {
            this._compiledOptionFun = this.coords.utils$.compileScriptFunction(this.script);
        } catch (error) {
            console.error('CONDITION FIELD SCRIPT ERROR', this.script, error);
        }
    }

    testItem(xitm: XItem) {
        if (!this.script) return '';
        if (!this._compiledOptionFun) this._compileOptionFun();
        if (!this._compiledOptionFun) return '';
        try {
            return this._compiledOptionFun(xitm.scriptClass);
        } catch (error: any) {
            console.log('FORMULA ERROR', this.script, error);
            return {
                isError: true,
                message: error.message,
            };
        }
    }

    getScript() {
        return this.script || '';
    }

    submitScript(data: string) {
        this.script = data;
        this._compileOptionFun();
        this.coords.display$.markForCheck();
        this.markForManifestStore();
    }

    public get isOn() {
        // return !this.coords.pin$.misc?.has(`${this.id}-off`);
        return this.meta.isOn !== false;
    }

    public set isOn(value) {
        // if (value) this.coords.pin$.misc?.off(`${this.id}-off`);
        // else this.coords.pin$.misc?.on(`${this.id}-off`);
        this.meta.isOn = value;
        this.markForManifestStore();

        this.coords.display$.markForCheck();
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    updateUserValues(dct: any) {
        this.onSetMap.clear();
        Object.keys(dct).forEach((k) => {
            this.onSetMap.set(k, new Set(dct[k]));
        });
    }

    getNodeFromPath(path: string, isCreate = false) {
        const ls = path.split('/').filter((x) => x);
        return this.root.getNodeFromPath(ls, isCreate);
    }

    getNodeFromId(nodeId: string) {
        return this.root.getNodeFromId(nodeId);
    }

    getNodeOnSet(nd: BinaryTreeNode) {
        let s = this.onSetMap.get(nd.id);
        if (!s) {
            s = new Set<string>();
            this.onSetMap.set(nd.id, s);
        }
        return s;
    }

    setNodeOnSet(nd: BinaryTreeNode, s: Set<string>) {
        this.onSetMap.set(nd.id, s);
    }

    get isSearchOn() {
        return !!(this.searchParams.includeSet.size || this.searchParams.excludeSet.size);
    }

    getCurrentUserStorage() {
        if (!this._bts) {
            const ownerId = this.coords.user$.currentId;

            this._bts = new BinaryTreeStorage(this.coords, {
                id: `bts-${this.id}-${ownerId}`,
                ownerId: ownerId,
                originId: this.id,
                valueDct: {},
            });
        }
        return this._bts;
    }

    reportValuesStore() {
        this.manifestValuesStore(true);
    }

    reset() {
        this.onSetMap.clear();
        this.manifestValuesStore();
    }

    values() {
        const dct: { [x: string]: string[] } = {};
        this.onSetMap.forEach((s, id) => {
            if (s.size) dct[id] = Array.from(s);
        });
        return dct;
    }
    manifestValuesStore(isReport = false) {
        this.getCurrentUserStorage().set(this.values(), isReport);
    }

    private _manifestStore() {
        const valueMap = new Map<string, any>([['bt', this.save()]]);

        this.coords.manifest$.store({
            type: 'bt',
            id: this.id,
            valueMap: valueMap,
        });

        this.manifestValuesStore();
    }

    get deleteParams() {
        return {
            icon: 'delete',
            fun: () => {
                this.delete();
                this.coords.display$.markForCheck();
            },
        };
    }

    manifestDelete() {
        const ls = [
            {
                id: this.id,
                type: 'bt',
            },
        ];
        const bts = this.getCurrentUserStorage();
        bts && ls.push(bts.manifestDelete());
        this.coords.manifest$.delete(ls);
        // this.coords.user$.cloudUpdateManager?.deleteFileLs(ls)
    }

    get isOwned() {
        return this.ownerId === this.coords.user$.currentId;
    }

    stats() {
        const rowLs: any[] = [];
        this.topLevelNodeLs.forEach((btn: BinaryTreeNode) => rowLs.push(btn.stats()));
        return rowLs;
    }

    delete() {
        this.coords.binaryTree$.delete(this);
        this.manifestDelete();
    }

    updateFromEditor(e: any) {
        this.coords.binaryTree$.setTree(this);
        this.title = e.title;
        this.root = new BinaryTreeNode(this, e.root.btnPar);
        // this.root.updateFromMap(this.bts.map())
        this._btEdit = undefined;
        this.isEditMode = false;
        this._manifestStore();
    }

    mainState(xitmId: string) {
        const ls: string[] = [];
        for (let child of this.root.childLs) if (child.isOn(xitmId)) ls.push(child.title);
        return ls.join(', ');
    }

    get searchTag() {
        return `_bt_${this.id}`;
    }

    get editor() {
        if (!this._btEdit) this._btEdit = new BinaryTreeEdit(this.coords, { bt: this });
        return this._btEdit;
    }

    public get isEditMode() {
        return this._isEditMode;
    }

    public set isEditMode(value) {
        this._isEditMode = value;
    }

    public get isModified() {
        return this._isModified;
    }

    public set isModified(value) {
        this._isModified = value;
    }

    get topLevelNodeLs() {
        return Array.from(this.root.childLs);
    }

    get title() {
        return this.meta.title || '';
    }

    set title(val: string) {
        this.meta.title = val;
    }

    get description() {
        return this.meta.description || '';
    }

    set description(val: string) {
        this.meta.description = val;
    }

    get type() {
        return this.meta.type;
    }

    exportJsonAll() {
        return {
            id: this.id,
            title: this.title,
            nodes: this.root.childLs.map((x) => x.exportJsonAll()),
        };
    }

    exportJsonAllXitem(xitemId: string) {
        const currentLs: string[] = [];
        this.root.descendants.forEach((btn) => {
            if (btn.isOn(xitemId)) currentLs.push(btn.id);
        });
        const userValues: any = {};
        const dct: any = { id: this.id, userValues };
        userValues[this.coords.user$.currentId] = currentLs;
        this.coords.project$.current?.team?.memberLs.forEach((m) => {
            if (m.isCurrentUser) return;
            userValues[m.id] = Array.from(m.otherBt(xitemId, this)?.nodeSet || []);
        });
        return dct;
    }

    export(isPartial = false) {
        const dct = this.save();
        const title = `${this.type}-${this.title}`;
        this.coords.saveFile(title, dct, false);
    }

    save(): BTPAR {
        return {
            id: this.id,
            meta: this.meta,
            root: this.root.save(),
        };
    }

    get sortTitle() {
        return this.title?.toLowerCase();
    }

    submitBulk(selectedNodesSet: Set<string>, au?: AutoUser) {
        const xitmIdLs = this.coords.resultList$.activeList$.getValue().map((ditm) => ditm.id);

        if (au) return au.submitBulkBt(xitmIdLs, this, selectedNodesSet);
        this.root.submitBulk(xitmIdLs, selectedNodesSet);
        this.manifestValuesStore();
        this.coords.bulk$.closeAccordion$.next();
        this.coords.display$.markForCheck();
    }

    get nodeMap() {
        const mp = new Map<string, BinaryTreeNode>();
        this.topLevelNodeLs.forEach((btn) => {
            mp.set(btn.id, btn);
            btn.descendants.forEach((btn) => mp.set(btn.id, btn));
        });

        return mp;
    }

    get scriptClass() {
        if (this.type === 'tags') return new ScriptTags(this.coords, this);
        else return new ScriptRatings(this.coords, this);
    }

    createNodesFromTemplate(ls: TEMPLATE_NODE[]) {
        ls.forEach((x) => {
            this.root.createNodeFromTemplate(x);
        });
    }
}
